import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import {
  ArchiveIssueSourceDocument,
  connectionRows,
  CreateIssueSourceDocument,
  DeleteIssueSourceDocument,
  GetIssueSourcesDocument,
  type IssueSourceRow,
  UnarchiveIssueSourceDocument,
  UpdateIssueSourceDocument,
} from "#app/lib/data";
import {
  addIssueSourceToCache,
  removeIssueSourceFromCache,
} from "./issue-source-cache-utils";
import { graphQLErrorMessage, toastMutationError } from "#app/lib/utils/toast-errors";
import { AnalyticsContext, SettingsEvent, trackEvent } from "#app/lib/analytics";

const trackSource = (
  event: (typeof SettingsEvent)[keyof typeof SettingsEvent],
  data?: Record<string, unknown>,
) => trackEvent({ context: AnalyticsContext.Settings, event, label: "issue_source", ...(data ? { data } : {}) });

export type IssueSource = IssueSourceRow;

// The whole pool in one page: the catalog is a word list (a workspace seeds
// 55 and grows by hand), and every consumer — the settings list, the picker
// — needs all of it. The server's default page (50) would silently hide the
// tail.
const ISSUE_SOURCES_QUERY_VARS = { limit: 500, orderBy: { name: 1 } };

export function useIssueSources() {
  const { data, loading, error, refetch } = useQuery(GetIssueSourcesDocument, {
    variables: ISSUE_SOURCES_QUERY_VARS,
    fetchPolicy: "cache-and-network",
  });

  const issueSources = useMemo<IssueSource[]>(
    () => connectionRows(data?.issueSources),
    [data],
  );

  return {
    issueSources,
    total: data?.issueSources.total ?? issueSources.length,
    isLoading: loading,
    error,
    refetch,
  };
}

export function useIssueSourceMutations() {
  const [createMutation] = useMutation(CreateIssueSourceDocument);
  const [updateMutation] = useMutation(UpdateIssueSourceDocument);
  const [archiveMutation] = useMutation(ArchiveIssueSourceDocument);
  const [unarchiveMutation] = useMutation(UnarchiveIssueSourceDocument);
  const [deleteMutation] = useMutation(DeleteIssueSourceDocument);

  const createIssueSource = useCallback(
    async (name: string) => {
      try {
        const { data } = await createMutation({
          variables: { input: { name } },
          update(cache, { data }) {
            const result = data?.createIssueSource;
            if (result?.__typename !== "IssueSource") return;
            addIssueSourceToCache(cache, result);
          },
        });
        const result = data?.createIssueSource;
        if (result?.__typename === "IssueSource") {
          trackSource(SettingsEvent.Create);
          return result;
        }
        if (result?.__typename === "ValidationError") {
          toastManager.add({ title: result.message ?? "Failed to create source", type: "error" });
          return null;
        }
        toastManager.add({ title: "Failed to create source", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to create source");
        return null;
      }
    },
    [createMutation],
  );

  const updateIssueSource = useCallback(
    async (id: string, name: string) => {
      try {
        const { data } = await updateMutation({
          variables: { id, input: { name } },
        });
        const result = data?.updateIssueSource;
        if (
          result?.__typename === "ValidationError" ||
          result?.__typename === "PermissionDenied"
        ) {
          toastManager.add({ title: result.message ?? "Failed to update source", type: "error" });
          return null;
        }
        if (result?.__typename === "IssueSource") {
          trackSource(SettingsEvent.Update);
          return result;
        }
        toastManager.add({ title: "Failed to update source", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to update source");
        return null;
      }
    },
    [updateMutation],
  );

  const toggleIssueSource = useCallback(
    async (source: IssueSource) => {
      try {
        const result = source.archived
          ? await unarchiveMutation({ variables: { id: source.id } })
          : await archiveMutation({ variables: { id: source.id } });
        // errorPolicy "all": a refusal resolves with `error`, not a throw.
        if (result.error) throw result.error;
        trackSource(source.archived ? SettingsEvent.Unarchive : SettingsEvent.Archive);
      } catch (err) {
        // The server names why it refused (the default source cannot be
        // disabled), so its words beat the generic fallback.
        toastMutationError(err, graphQLErrorMessage(err) ?? "Failed to update source");
      }
    },
    [archiveMutation, unarchiveMutation],
  );

  const deleteIssueSource = useCallback(
    async (source: IssueSource) => {
      try {
        await deleteMutation({
          variables: { id: source.id },
          update(cache, { data }) {
            const result = data?.deleteIssueSource;
            if (result?.__typename !== "IssueSource") return;
            removeIssueSourceFromCache(cache, result);
          },
        });
        trackSource(SettingsEvent.Delete);
      } catch (err) {
        toastMutationError(err, "Failed to delete source");
      }
    },
    [deleteMutation],
  );

  return {
    createIssueSource,
    updateIssueSource,
    toggleIssueSource,
    deleteIssueSource,
  };
}
